/**
 * `OfflineScreen` — shown when no agents are online / outside business hours
 * (ARCHITECTURE §7). Old-cockpit parity (``agent_offline_inquiry``): a
 * leave-a-message form whose submission becomes a support TICKET plus an
 * inquiry email to the org owner and a confirmation email to the visitor.
 */

import { useState, type FormEvent } from 'react';
import type { WidgetConfig } from '@flozic/shared/types';
import { getWidgetApi } from '../config/loadWidgetConfig';

export interface OfflineScreenProps {
  config: WidgetConfig | null;
  /** Embed id (`ORG-WIDGET`) — the offline message posts against it. */
  cid: string;
}

export function OfflineScreen({ config, cid }: OfflineScreenProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    !sending && name.trim().length > 0 && /\S+@\S+\.\S+/.test(email) && message.trim().length > 0;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSending(true);
    setError(null);
    try {
      await getWidgetApi().widget.offlineMessage(cid, {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
      });
      setSent(true);
    } catch {
      setError("Couldn't send your message. Please try again.");
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <div className="grid h-12 w-12 place-items-center rounded-full bg-lc-primary/15 text-2xl" aria-hidden>
          ✓
        </div>
        <p className="text-sm font-medium text-lc-text">Thanks, {name.trim() || 'friend'}!</p>
        <p className="text-sm text-lc-text/70">
          We've received your message and will get back to you at{' '}
          <span className="font-medium text-lc-text">{email.trim()}</span>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex h-full flex-col gap-3 overflow-y-auto p-4">
      <div className="text-center">
        <div className="text-2xl" aria-hidden>
          💤
        </div>
        <p className="mt-1 text-sm text-lc-text/80">
          {config?.offlineMessage ??
            "We're offline right now. Leave a message and we'll get back to you."}
        </p>
      </div>
      <label className="text-xs font-medium text-lc-text/70">
        Name
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={200}
          className="mt-1 w-full rounded-lc bg-black/5 px-3 py-2 text-sm text-lc-text outline-none focus:ring-2 focus:ring-lc-primary/40"
        />
      </label>
      <label className="text-xs font-medium text-lc-text/70">
        Email
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          maxLength={320}
          className="mt-1 w-full rounded-lc bg-black/5 px-3 py-2 text-sm text-lc-text outline-none focus:ring-2 focus:ring-lc-primary/40"
        />
      </label>
      <label className="flex min-h-0 flex-1 flex-col text-xs font-medium text-lc-text/70">
        How can we help?
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          required
          maxLength={2000}
          rows={4}
          className="mt-1 w-full flex-1 resize-none rounded-lc bg-black/5 px-3 py-2 text-sm text-lc-text outline-none focus:ring-2 focus:ring-lc-primary/40"
        />
      </label>
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
      <button
        type="submit"
        disabled={!canSubmit}
        className="rounded-lc bg-lc-primary px-4 py-2.5 text-sm font-medium text-lc-on-primary disabled:opacity-50"
      >
        {sending ? 'Sending…' : 'Send message'}
      </button>
    </form>
  );
}
